<template>
  <div>
    <label class="input-label" v-if="label">{{ label }}</label>
    <v-text-field
      ref="profileAddressInput"
      :value="input"
      @input="$emit('input', $event)"
      :label="label ? '' : 'Enter address'" 
      :dense="dense"
      :outlined="outlined"
      prepend-inner-icon="mdi-map-marker"
      :rules="getRules"
      v-bind="$attrs"
      :class="[
        label ? 'mt-1' : '',
        'py-1',
        'pr-16',
        'pl-5',
        'mt-2.5',
        'whitespace-nowrap',
        'bg-slate-100',
        'rounded-[32px]',
        'text-slate-500',
        'max-md:pr-5',
         $attrs.class, // Preserve existing classes from $attrs
      ]"
      id="profile-address-input"
    >
      <template v-slot:append>
        <v-tooltip bottom v-if="help">
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon v-bind="attrs" v-on="on" class="" x-small>
              <v-icon>mdi-help-circle-outline</v-icon>
            </v-btn>
          </template>
          <span>{{ help }}</span>
        </v-tooltip>
      </template>
    </v-text-field>
  </div>
</template>

<script>
import { getRules } from "@/utils/common.utils";

export default {
  inheritAttrs: false,
  props: {
    label: {
      type: String,
      default: "",
    },
    value: { 
      type: [String, Number],
      default: "",
    },
    help: {
      type: String,
      default: "",
    },
    dense: {
      type: Boolean,
      default: true,
    },
    outlined: {
      type: Boolean,
      // Default outlined to false if it was implied by vuetify-google-autocomplete's default styling
      // Or keep true if v-text-field should be outlined by default.
      // vuetify-google-autocomplete doesn't have an explicit outlined prop in its docs, assumes it inherits from v-text-field.
      // Given the styling classes, it seems it was intended to be a custom styled input, not necessarily outlined.
      // Let's assume false and let users override via $attrs or specific prop if needed.
      default: false, 
    },
    rules: {
      type: String,
      default: "",
    },
    customRules: {
      type: Array,
      default: () => [],
    },
    autocompleteOptions: {
      type: Object,
      default: () => ({
        fields: ["address_components", "formatted_address", "geometry", "name", "place_id"],
        types: ["address"], 
      }),
    },
  },
  data() {
    return {
      autocomplete: null,
    };
  },
  computed: {
    getRules() {
      let { customRules, rules } = this;
      if (customRules.length > 0) return customRules;
      return getRules(rules);
    },
    input: {
        get() {
            return this.value;
        },
        set(val) {
            this.$emit('input', val);
        }
    }
  },
  mounted() {
    this.initAutocomplete();
  },
  methods: {
    initAutocomplete() {
      this.$nextTick(async () => {
        if (!window.google || !window.google.maps || !window.google.maps.places) {
          console.warn("Google Maps Places API not loaded for ProfileAddress.vue");
          return;
        }

        const inputElement = this.$refs.profileAddressInput?.$el?.querySelector('input');
        if (!inputElement) {
          console.error("ProfileAddress.vue: Input element not found for Autocomplete.");
          return;
        }
        
        try {
            this.autocomplete = new window.google.maps.places.PlaceAutocompleteElement({
                inputElement: inputElement,
                ...this.autocompleteOptions,
            });

            this.autocomplete.addEventListener("gmp-placeselect", async (event) => {
                const place = event.place;
                if (!place || !place.id) {
                    this.$emit("onAddress", null, null); 
                    return;
                }
                
                const fieldsToFetch = this.autocompleteOptions.fields || ['address_components', 'formatted_address', 'geometry', 'name', 'place_id'];
                if (place.fetchFields) {
                    await place.fetchFields({ fields: fieldsToFetch });
                }

                const addressData = {
                    street_number: place.addressComponents?.find(c => c.types.includes('street_number'))?.longText || '',
                    route: place.addressComponents?.find(c => c.types.includes('route'))?.longText || '',
                    locality: place.addressComponents?.find(c => c.types.includes('locality'))?.longText || '',
                    administrative_area_level_1: place.addressComponents?.find(c => c.types.includes('administrative_area_level_1'))?.shortText || '',
                    country: place.addressComponents?.find(c => c.types.includes('country'))?.longText || '',
                    postal_code: place.addressComponents?.find(c => c.types.includes('postal_code'))?.longText || '',
                    latitude: place.geometry?.location?.lat(),
                    longitude: place.geometry?.location?.lng(),
                    name: place.name,
                    formatted_address: place.formattedAddress,
                    place_id: place.id,
                };
                
                this.$emit('input', place.formattedAddress);
                this.$emit("onAddress", addressData, place); 
            });
        } catch (error) {
            console.error("Error initializing PlaceAutocompleteElement in ProfileAddress:", error);
        }
      });
    },
  },
};
</script>
